import type { AasApiAdapter } from "../adapter";
import { isHttpError, requestJson } from "../http";
import type { SandboxClass } from "../types";
import type { BackendSandboxClass } from "./backendTypes";
import { toNumber } from "./shared";

export function mapSandboxClass(row: BackendSandboxClass): SandboxClass {
	return {
		id: row.id,
		workspaceId: row.workspaceId,
		name: row.name,
		description: row.description ?? "",
		maxCpus: toNumber(row.maxCpus),
		maxMemMb: toNumber(row.maxMemMb),
		maxPids: toNumber(row.maxPids),
		maxFsizeMb: toNumber(row.maxFsizeMb),
		maxDiskMb: toNumber(row.maxDiskMb, 2048),
		maxTimeSec: toNumber(row.maxTimeSec),
		// The detail endpoint does not count agents; only the list does.
		agentCount: toNumber(row.agentCount),
		createdAt: row.createdAt,
		updatedAt: row.updatedAt,
	};
}

export const sandboxClassesApi: AasApiAdapter["sandboxClasses"] = {
	async list() {
		const rows = await requestJson<BackendSandboxClass[]>("/api/sandbox-classes");
		return rows.map(mapSandboxClass);
	},
	async get(id) {
		try {
			const row = await requestJson<BackendSandboxClass>(
				`/api/sandbox-classes/${encodeURIComponent(id)}`,
			);
			return mapSandboxClass(row);
		} catch (error) {
			if (isHttpError(error) && error.status === 404) return null;
			throw error;
		}
	},
	async create(input) {
		const row = await requestJson<BackendSandboxClass>("/api/sandbox-classes", {
			method: "POST",
			body: input,
		});
		return mapSandboxClass(row);
	},
	async update(id, input) {
		const row = await requestJson<BackendSandboxClass>(
			`/api/sandbox-classes/${encodeURIComponent(id)}`,
			{ method: "PATCH", body: input },
		);
		return mapSandboxClass(row);
	},
	async remove(id) {
		await requestJson<void>(`/api/sandbox-classes/${encodeURIComponent(id)}`, {
			method: "DELETE",
		});
	},
};
